import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Form,
  Container,
  Row,
  Col,
  Image,
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import ExternalRegistrationService from "../../services/externalRegistration.service";
import { ValidationResponse, ExternalRegistration } from "../../models/ExternalRegistration";
import { Credentials } from "../../models/Credentials";
import LoginService from "../../services/login.service";
import { UserInfo } from "../../models/UserInfo";
import PropTypes from "prop-types";
import useUser from "../App/useUser";

type Props = {
  setUser: (user: UserInfo) => void;
};

export default function ExternalRegister(props: Props) {
  const { user, isLoading } = useUser();
  const { questionaryId } = useParams<{ questionaryId: string }>();
  const navigate = useNavigate();
  const [validated, setValidated] = useState(false);
  const [showValidationError, setShowValidationError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorTitle, setErrorTitle] = useState("");
  const [formData, setFormData] = useState<ExternalRegistration>({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    questionaryId: ""
  });
  const [isValidating, setIsValidating] = useState(true);
  const [isValidQuestionary, setIsValidQuestionary] = useState(false);
  var emailRegistered = "";

  useEffect(() => {
    let isMounted = true;
    const validateQuestionary = async () => {
      try {
        if (!questionaryId) throw new Error("El cuestionario no es valido, favor de verificar el enlace");
        
        const response = await ExternalRegistrationService.validateQuestionaryUser(questionaryId);

        if (response.questionaryId == "") throw new Error("El cuestionario no es valido, favor de verificar el enlace");

        if (isLoading) return;

        if(response){
          emailRegistered = response.email;
        }

        if(user)
        {
          if (user.role == "Administrador" || user.email == response.email){
            localStorage.setItem('questionaryId', response.questionaryId);
            navigate("/FillQuestionary");//Here we need to send directly to the questionary
          }
          else
          {
            setErrorMessage("La cuenta con la que estás logueado no corresponde al cuestionario, verifica tu cuenta y el enlace");
            setShowValidationError(true);
          }
        }
        else if (response.isRegistred) {
          localStorage.setItem('questionaryId', response.questionaryId);
          localStorage.setItem('questionaryEmail', emailRegistered);
          navigate("/login");
        } else {
          setIsValidQuestionary(true);
          setFormData(prev => ({
            ...prev,
            name: response.name,
            email: response.email,
            questionaryId: questionaryId
          }));
        }        
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : "El cuestionario no es valido, favor de verificar el enlace");
        setShowValidationError(true);
      } finally {
        setIsValidating(false);
      }
    };

    validateQuestionary();
    return() => { isMounted = false };
  }, [questionaryId, navigate, user, isLoading]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const form = event.currentTarget as HTMLFormElement;
    setValidated(true);

    if (form.checkValidity() === false || formData.password !== formData.confirmPassword) {
      const passwordValid = formData.password.length >= 8;
  
      setErrorMessage(
        !passwordValid ? "La contraseña debe tener al menos 8 caracteres" :
        form.checkValidity() === false ? 
          "Es necesario llenar todos los campos correctamente" 
          : "Las contraseñas ingresadas no son iguales"
      );
      setShowValidationError(true);
      window.scrollTo(0, 0);
      return;
    }

    try {
      const registrationData: ExternalRegistration = {
        ...formData
      };

      const response = await ExternalRegistrationService.registerExternalUser(registrationData);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Error en el registro");
      }
      
      let credentials: Credentials = { username: registrationData.email, password: registrationData.password };
      const result = LoginService.Login(credentials);

      result
      .then((response) => {
        var resp = response as unknown as Response;

        if (resp.ok) {
          return resp.json();
        } 
        else {
          setErrorMessage("Nombre de usuario o contraseña incorrecta");
          setShowValidationError(true);
          setErrorTitle("Error en inicio de sesión");
        }
      })
      .then((json) => {
        props.setUser(json);
        var questionary = questionaryId == undefined ? "": questionaryId;
        localStorage.setItem('questionaryId', questionary);
        localStorage.setItem('questionaryEmail', emailRegistered);
        if (emailRegistered == json.email)
        {
          navigate("/FillQuestionary");
        }
        else
        {
          navigate("/");
        }
        
      });

    } catch (error) {
      setErrorMessage(error instanceof Error 
        ? error.message 
        : "Error durante el registro, favor de contactar al administrador");
      setShowValidationError(true);
      window.scrollTo(0, 0);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (isLoading || isValidating) {
    return (
      <Container className="mt-5">
        <Row>
          <Col md={{ span: 6, offset: 3 }}>
            <Card>
              <Card.Body className="text-center">
                <div className="d-flex align-items-center justify-content-center">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <span className="ms-2">Validando cuestionario...</span>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  if (!isValidQuestionary) {
    return (
      <Container className="mt-5">
        <Row>
          <Col md={{ span: 6, offset: 3 }}>
            <Alert variant="danger">
              <Alert.Heading>{errorTitle}</Alert.Heading>
              <p>{errorMessage}</p>
            </Alert>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="mt-5">
      <Row>
        <Col md={{ span: 6, offset: 3 }}>
          <div className="text-center">
            <div className="alert alert-info">
              <p className="lead mb-0">
                <i className="bi bi-exclamation-circle-fill me-2"></i>
                Favor de registrarse para contestar el cuestionario.
              </p>
            </div>
          </div>
          {showValidationError && (
            <Alert
              variant="danger"
              onClose={() => setShowValidationError(false)}
              dismissible
            >
              <Alert.Heading>{errorTitle}</Alert.Heading>
              <p>{errorMessage}</p>
            </Alert>
          )}
          <Card>
            <Card.Body>
              <Row>
                <Col className="text-center">
                  <Image src="/logo-legacy.png" fluid />
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form noValidate validated={validated} onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label>Nombre</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Ingresa tu nombre"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="Ingresa tu correo electrónico"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Contraseña</Form.Label>
                      <Form.Control
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        required
                        minLength={8}
                        placeholder="Ingresa una contraseña"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Confirmar contraseña</Form.Label>
                      <Form.Control
                        type="password"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        required
                        minLength={8}
                        placeholder="Ingresa tu contraseña nuevamente"
                      />
                    </Form.Group>

                    <div className="d-grid gap-2">
                      <Button variant="primary" type="submit">
                        Completar Registro
                      </Button>
                    </div>
                  </Form>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

ExternalRegister.Props = {
  setUser: PropTypes.func.isRequired,
};
